import type {
  CycleCountFrequency,
  CycleCountScopeType,
} from '@rematter/frontend-lib-api-client';

import { createQueryAtomFactory } from '~/shared/providers/api/createQueryAtomFactory';

export type CycleCountScheduleSortOption =
  | 'NameAsc'
  | 'NameDesc'
  | 'FrequencyAsc'
  | 'FrequencyDesc'
  | 'CreatedAtAsc'
  | 'CreatedAtDesc';

export const cycleCountSchedulesQueryAtom = createQueryAtomFactory(
  'inventory.cycleCountSchedules',
  (params: {
    facilityIds?: string[];
    limit?: number;
    offset?: number;
    sorting?: CycleCountScheduleSortOption[];
  }) => ({
    cycleCountSchedules: {
      __args: {
        filter: {
          facilityIds: params.facilityIds,
        },
        sorting: params.sorting,
        pagination: {
          limit: params.limit ?? 25,
          offset: params.offset ?? 0,
        },
      },
      entries: {
        id: true,
        facilityId: true,
        facility: {
          id: true,
          facilityName: true,
        },
        name: true,
        frequency: true,
        scopeType: true,
        isBlindCount: true,
        defaultTolerancePercentage: true,
        isActive: true,
        lastGeneratedAt: true,
        nextGenerationAt: true,
        timestamps: {
          createdAt: true,
          updatedAt: true,
        },
      },
      page: {
        total: true,
      },
    },
  }),
  {
    select: (
      data,
    ): {
      schedules: CycleCountScheduleRecord[];
      totalCount: number;
    } => {
      if (
        !data ||
        !data.cycleCountSchedules ||
        !data.cycleCountSchedules.entries
      ) {
        return {
          schedules: [],
          totalCount: 0,
        };
      }

      const schedules = data.cycleCountSchedules.entries.map(
        (schedule: Record<string, unknown>) => ({
          id: schedule.id as string,
          facilityId: schedule.facilityId as string,
          facilityName:
            ((schedule.facility as Record<string, unknown>)
              ?.facilityName as string) || '',
          name: schedule.name as string,
          frequency: schedule.frequency as CycleCountFrequency,
          scopeType: schedule.scopeType as CycleCountScopeType,
          isBlindCount: schedule.isBlindCount as boolean,
          defaultTolerancePercentage:
            schedule.defaultTolerancePercentage as string,
          isActive: schedule.isActive as boolean,
          lastGeneratedAt: (schedule.lastGeneratedAt as string) || null,
          nextGenerationAt: (schedule.nextGenerationAt as string) || null,
          createdAt:
            ((schedule.timestamps as Record<string, unknown>)
              ?.createdAt as string) || '',
          updatedAt:
            ((schedule.timestamps as Record<string, unknown>)
              ?.updatedAt as string) || '',
        }),
      );

      return {
        schedules,
        totalCount: (data.cycleCountSchedules.page?.total as number) || 0,
      };
    },
  },
);

export type CycleCountScheduleRecord = {
  id: string;
  facilityId: string;
  facilityName: string;
  name: string;
  frequency: CycleCountFrequency;
  scopeType: CycleCountScopeType;
  isBlindCount: boolean;
  defaultTolerancePercentage: string;
  isActive: boolean;
  lastGeneratedAt: string | null;
  nextGenerationAt: string | null;
  createdAt: string;
  updatedAt: string;
};
